import React, { useState, useRef, useEffect } from 'react';
import {
  Mic, Send, CheckCircle2, X, Loader2, Calendar,
  DollarSign, Receipt, Bot, User, Paperclip, ShieldCheck, HardHat, AlertTriangle, FileDown,
} from 'lucide-react';
import { apiService } from '@/lib/api';

/**
 * AssistantChat — reusable conversation body for the DrawReady Assistant.
 * Used by both the full-page preview and the floating widget.
 * When `live` is true it calls the real /api/assistant endpoints; otherwise it
 * falls back to keyword parsing + canned answers for standalone demos.
 */

export const NAVY = '#0b3b6f';
export const GREEN = '#5c9e33';

// Lightweight markdown renderer: **bold** + line breaks only (no tables/headings).
function RichText({ text }) {
  if (!text) return null;
  const lines = String(text).split('\n');
  return (
    <>
      {lines.map((line, li) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
        return (
          <span key={li}>
            {parts.map((p, pi) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={pi}>{p.slice(2, -2)}</strong>
                : <span key={pi}>{p}</span>
            )}
            {li < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
}


const EXAMPLES = [
  'Electrical is slated to start rough-in work for the Ellis Row project',
  "Let's pay Johnson Plumbing $5,000 as a partial payment for work completed on 1615 38th St",
  'Add $380.65 to lumber materials and attach this receipt to the phase',
  'How much interest is due on 1615 38th St?',
];

// Canned Q&A answers (demonstrates the "ask it anything project-related" side).
function fakeAnswer(t) {
  if (t.includes('interest') && t.includes('due')) {
    return 'On 1615 38th St SE, interest due is $4,775.12 (accrued $8,100.02 − $3,324.90 paid). Current per-diem is $61.04/day at 7.375%.';
  }
  if (t.includes('owe') || t.includes('unpaid') || t.includes('who')) {
    return 'On 1615 38th St SE you have 2 subs awaiting payment: Johnson Plumbing ($5,000 rough-in) and Ellis Electric ($3,200 rough-in). Both have a valid W-9 and COI on file.';
  }
  if (t.includes('draw') || t.includes('available')) {
    return "You've drawn $308,421 of the $360,000 loan — $51,408 remains available for draw on 1615 38th St SE.";
  }
  return null;
}

// Fake "AI parse" → returns a structured action card (mock only).
function fakeParse(text) {
  const t = text.toLowerCase();
  if (t.includes('pay') && (t.includes('$') || t.includes('000') || t.includes('partial') || t.includes('final'))) {
    const isFinal = t.includes('final');
    return {
      kind: 'payment', title: 'Record a payment', icon: DollarSign, tone: GREEN,
      fields: [
        ['Action', 'Record sub/vendor payment'],
        ['Payee', 'Johnson Plumbing'],
        ['Project', '1615 38th St SE'],
        ['Phase › Component', 'Phase 4 · Rough-in › Plumbing'],
        ['Amount', '$5,000.00'],
        ['Payment type', isFinal ? 'Final payment' : 'Partial payment'],
      ],
      compliance: [
        isFinal ? 'Final (unconditional) lien waiver sent to Johnson Plumbing'
                : 'Partial (conditional) lien waiver sent to Johnson Plumbing',
        'W-9 on file ✓',
        'COI valid through 11/30/2026 ✓',
      ],
      done: `Recorded a $5,000.00 ${isFinal ? 'final' : 'partial'} payment to Johnson Plumbing and started the compliance process.`,
    };
  }
  if ((t.includes('add') || t.includes('cost')) && (t.includes('material') || t.includes('lumber') || t.includes('labor') || t.includes('$'))) {
    const isLabor = t.includes('labor');
    return {
      kind: 'cost', title: 'Add a cost', icon: Receipt, tone: '#b45309', needsReceipt: true,
      fields: [
        ['Action', `Add ${isLabor ? 'labor' : 'material'} cost`],
        ['Phase › Component', 'Phase 3 · Framing › Lumber'],
        ['Cost type', isLabor ? 'Labor' : 'Material'],
        ['Amount', '$380.65'],
      ],
      done: 'Added $380.65 to Lumber (Material) under Framing and attached the receipt.',
    };
  }
  return {
    kind: 'schedule', title: 'Schedule a component', icon: Calendar, tone: NAVY,
    fields: [
      ['Action', 'Update schedule'],
      ['Project', 'Ellis Row'],
      ['Phase', 'Phase 4 · Exterior & Systems (Rough-in)'],
      ['Component', 'Electrical'],
      ['Milestone', 'Rough-in start'],
      ['Start date', '08/04/2026'],
    ],
    done: 'Scheduled Electrical rough-in to start 08/04/2026 in Phase 4 on Ellis Row (added to the project schedule).',
  };
}

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800 text-right">{value}</span>
  </div>
);

function ActionCard({ msg, onConfirm, onCancel, live, userId, smartReceipt = true }) {
  const Icon = msg.card.icon;
  const [state, setState] = useState('pending'); // pending | committing | receipt | checking | receipt-mismatch | receipt-duplicate | done
  const [resultMsg, setResultMsg] = useState(msg.card.done);
  const [validation, setValidation] = useState(null);
  const fileRef = useRef(null);

  const commit = async () => {
    setState('committing');
    if (live) {
      try {
        const res = await apiService.assistantExecute(userId, msg.action);
        const data = res.data || {};
        if (data.ok === false) {
          setResultMsg(data.message || 'Could not complete the action.');
          setState('done');
          onConfirm(data.message);
          return;
        }
        setResultMsg(data.message || msg.card.done);
      } catch (e) {
        setResultMsg('Something went wrong applying that. Please try again.');
        setState('done');
        onConfirm();
        return;
      }
    }
    setTimeout(() => {
      setState(msg.card.needsReceipt ? 'receipt' : 'done');
      if (!msg.card.needsReceipt) onConfirm();
    }, live ? 0 : 900);
  };

  const finishAttached = (extraNote) => {
    setResultMsg(`${msg.card.done}${extraNote ? ' ' + extraNote : ''}`);
    setState('done');
    onConfirm();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!(live && msg.action?.phase_id)) { setState('done'); onConfirm(); return; }
    // Gate off → legacy blind attach (no AI validation / duplicate check).
    if (!smartReceipt) {
      try { await apiService.uploadPhaseReceipt(msg.action.phase_id, file, { addToActualCost: false }); } catch (_) { /* non-blocking */ }
      finishAttached('Receipt attached.');
      return;
    }
    setState('checking');
    try {
      const res = await apiService.assistantReceiptCheck(msg.action.phase_id, userId, file);
      const d = res.data || {};
      if (d.status === 'duplicate') { setValidation(d); setState('receipt-duplicate'); return; }
      if (d.status === 'project_mismatch') { setValidation(d); setState('receipt-project-mismatch'); return; }
      if (d.status === 'mismatch') { setValidation(d); setState('receipt-mismatch'); return; }
      finishAttached('Receipt attached.');
    } catch (_) {
      finishAttached('(Receipt could not be validated — attach it from the project view.)');
    }
  };

  const attachAnyway = async () => {
    setState('committing');
    try {
      const res = await apiService.assistantReceiptCommit({
        user_id: userId, phase_id: msg.action.phase_id,
        file_id: validation?.staged_file_id, file_hash: validation?.file_hash,
        file_name: validation?.file_name, extracted: validation?.extracted,
      });
      if (res.data?.status !== 'attached') throw new Error('commit-failed');
      finishAttached('Receipt attached (phase mismatch overridden).');
    } catch (_) {
      setValidation(null);
      setResultMsg('The cost was saved, but the receipt could not be attached. Please attach it from the project view.');
      setState('done');
      onConfirm();
    }
  };

  const pickDifferent = async () => {
    if (validation?.staged_file_id) { try { await apiService.assistantReceiptDiscard(validation.staged_file_id); } catch (_) { /* ignore */ } }
    setValidation(null);
    setState('receipt');
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const cancelReceipt = async () => {
    if (validation?.staged_file_id) { try { await apiService.assistantReceiptDiscard(validation.staged_file_id); } catch (_) { /* ignore */ } }
    setValidation(null);
    setState('done');
    onConfirm();
  };

  const attachReceipt = () => { if (live && fileRef.current) { fileRef.current.click(); } else { setState('done'); onConfirm(); } };
  const skipReceipt = () => { setState('done'); onConfirm(); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="assistant-action-card">
      <div className="px-4 py-3 flex items-center gap-2 text-white" style={{ background: msg.card.tone }}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-semibold">{msg.card.title}</span>
        {state === 'done' && <CheckCircle2 className="w-4 h-4 ml-auto" />}
      </div>
      <div className="px-4 py-2 divide-y divide-slate-100">
        {msg.card.fields.map(([l, v]) => <Field key={l} label={l} value={v} />)}
      </div>
      {msg.card.impact && state === 'pending' && (
        <div className="px-4 pb-2 -mt-1">
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> {msg.card.impact}
          </p>
        </div>
      )}
      {state === 'pending' && (
        <div className="px-4 py-3 bg-slate-50 flex items-center gap-2 border-t border-slate-100">
          <button onClick={commit} data-testid="assistant-confirm" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: GREEN }}>
            <CheckCircle2 className="w-4 h-4" /> Confirm
          </button>
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-100 ml-auto" data-testid="assistant-cancel">
            <X className="w-4 h-4" /> Cancel
          </button>
        </div>
      )}
      {state === 'committing' && (
        <div className="px-4 py-3 bg-slate-50 flex items-center gap-2 text-slate-500 text-sm border-t border-slate-100">
          <Loader2 className="w-4 h-4 animate-spin" /> Applying…
        </div>
      )}
      {state === 'receipt' && (
        <div className="px-4 py-4 bg-amber-50 border-t border-amber-100" data-testid="assistant-receipt-step">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFilePicked} data-testid="assistant-receipt-input" />
          <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" /> Cost saved — attach a receipt to this phase?
          </p>
          <div className="rounded-lg border-2 border-dashed border-amber-300 bg-white px-3 py-4 flex flex-col items-center gap-1 mb-3">
            <Receipt className="w-6 h-6 text-amber-400" />
            <span className="text-xs text-slate-500">Take a photo or upload a receipt</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={attachReceipt} data-testid="assistant-attach-receipt" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: '#b45309' }}>
              <Paperclip className="w-4 h-4" /> Attach receipt
            </button>
            <button onClick={skipReceipt} className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-white">Skip for now</button>
          </div>
        </div>
      )}
      {state === 'checking' && (
        <div className="px-4 py-4 bg-amber-50 border-t border-amber-100 flex items-center gap-2 text-amber-800 text-sm" data-testid="assistant-receipt-checking">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading the receipt & checking for duplicates…
        </div>
      )}
      {state === 'receipt-duplicate' && (
        <div className="px-4 py-4 bg-red-50 border-t border-red-100" data-testid="assistant-receipt-duplicate">
          <p className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-4 h-4" /> Duplicate receipt — blocked
          </p>
          <p className="text-xs text-red-700 mb-2">{validation?.message}</p>
          {validation?.existing?.location && (
            <div className="rounded-lg bg-white border border-red-100 px-3 py-2 mb-3 text-xs text-slate-600">
              Already filed under <span className="font-semibold text-slate-800">{validation.existing.location}</span>
              {validation.existing.vendor ? ` · ${validation.existing.vendor}` : ''}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={pickDifferent} data-testid="assistant-receipt-pick-different" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: '#b45309' }}>
              <Paperclip className="w-4 h-4" /> Choose a different file
            </button>
            <button onClick={skipReceipt} className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-white">Skip</button>
          </div>
        </div>
      )}
      {state === 'receipt-project-mismatch' && (
        <div className="px-4 py-4 bg-red-50 border-t border-red-100" data-testid="assistant-receipt-project-mismatch">
          <p className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4" /> This receipt looks like a different project
          </p>
          <p className="text-xs text-red-700 mb-3">{validation?.message}</p>
          <div className="rounded-lg bg-white border border-red-100 divide-y divide-red-50 mb-3 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-500">Receipt is labeled for</span>
              <span className="font-semibold text-slate-800">{validation?.receipt_looks_like_project || '—'}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-500">You&apos;re filing under</span>
              <span className="font-semibold text-slate-800">{validation?.filing_under_project || '—'}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={attachAnyway} data-testid="assistant-receipt-project-attach-anyway" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: '#b45309' }}>
              <Paperclip className="w-4 h-4" /> Attach anyway (vendor mislabeled it)
            </button>
            <button onClick={pickDifferent} data-testid="assistant-receipt-project-different" className="px-3 py-2 rounded-lg text-sm font-semibold text-red-700 hover:bg-white">Use a different file</button>
            <button onClick={cancelReceipt} data-testid="assistant-receipt-project-cancel" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-white ml-auto">Cancel</button>
          </div>
        </div>
      )}

      {state === 'receipt-mismatch' && (
        <div className="px-4 py-4 bg-amber-50 border-t border-amber-100" data-testid="assistant-receipt-mismatch">
          <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4" /> This receipt may be for a different phase
          </p>
          <p className="text-xs text-amber-800 mb-3">{validation?.message}</p>
          <div className="rounded-lg bg-white border border-amber-100 divide-y divide-amber-50 mb-3 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-500">Receipt looks like</span>
              <span className="font-semibold text-slate-800">
                {validation?.receipt_looks_like?.phase_number ? `Phase ${validation.receipt_looks_like.phase_number}` : '—'}
                {validation?.receipt_looks_like?.phase_name ? ` · ${validation.receipt_looks_like.phase_name}` : ''}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-500">You&apos;re filing under</span>
              <span className="font-semibold text-slate-800">
                {validation?.filing_under?.phase_number ? `Phase ${validation.filing_under.phase_number}` : '—'}
                {validation?.filing_under?.phase_name ? ` · ${validation.filing_under.phase_name}` : ''}
                {validation?.filing_under?.component ? ` › ${validation.filing_under.component}` : ''}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={attachAnyway} data-testid="assistant-receipt-attach-anyway" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: '#b45309' }}>
              <Paperclip className="w-4 h-4" /> Attach anyway
            </button>
            <button onClick={pickDifferent} data-testid="assistant-receipt-mismatch-different" className="px-3 py-2 rounded-lg text-sm font-semibold text-amber-700 hover:bg-white">Choose a different file</button>
            <button onClick={cancelReceipt} data-testid="assistant-receipt-mismatch-cancel" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-white ml-auto">Cancel</button>
          </div>
        </div>
      )}
      {state === 'done' && (
        <div className="border-t border-emerald-100">
          <div className="px-4 py-3 bg-emerald-50 flex items-center gap-2 text-emerald-700 text-sm">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {resultMsg}
          </div>
          {msg.card.compliance && (
            <div className="px-4 py-3 bg-white border-t border-slate-100" data-testid="assistant-compliance">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Compliance process started
              </p>
              <ul className="space-y-1.5">
                {msg.card.compliance.map((c, i) => (
                  <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {c}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-slate-400 mt-2">Payment releases once the signed lien waiver is returned.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AssistantChat({ heightClass = 'h-[52vh]', showExamples = true, showFootnote = true, live = false, userId = null, smartReceipt = true }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm your DrawReady Assistant. Ask me anything about your projects, or tell me what to do — schedule a phase, record a payment, or add a cost with a receipt. Type it or tap the mic." },
  ]);
  const [input, setInput] = useState('');
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, transcribing]);

  const historyForApi = () => messages.filter((m) => m.text).slice(-6).map((m) => ({ role: m.role, text: m.text }));

  const submit = async (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: value }, { role: 'assistant', thinking: true }]);

    if (live) {
      try {
        const res = await apiService.assistantChat(userId, value, historyForApi());
        const d = res.data || {};
        setMessages((m) => {
          const copy = m.filter((x) => !x.thinking);
          if (d.type === 'confirm' && d.card) {
            const iconMap = { payment: DollarSign, cost: Receipt, schedule: Calendar };
            const card = { ...d.card, icon: iconMap[d.card.kind] || CheckCircle2 };
            return [...copy,
              { role: 'assistant', text: "Got it — here's what I'll do. Confirm to apply:" },
              { role: 'assistant', card, action: d.action }];
          }
          if (d.type === 'report' && d.report) {
            return [...copy, { role: 'assistant', text: d.text, report: d.report }];
          }
          return [...copy, { role: 'assistant', text: d.text || 'How can I help with your projects?' }];
        });
      } catch (e) {
        setMessages((m) => [...m.filter((x) => !x.thinking), { role: 'assistant', text: 'Sorry — I hit an error. Please try again.' }]);
      }
      return;
    }

    // mock mode
    const answer = fakeAnswer(value.toLowerCase());
    setTimeout(() => {
      setMessages((m) => {
        const copy = m.filter((x) => !x.thinking);
        if (answer) return [...copy, { role: 'assistant', text: answer }];
        return [...copy,
          { role: 'assistant', text: "Got it — here's what I'll do. Confirm to apply:" },
          { role: 'assistant', card: fakeParse(value) }];
      });
    }, 1100);
  };

  const onConfirmCard = (msgText) => setMessages((m) => [...m, { role: 'assistant', text: msgText || 'Done ✅ Anything else?' }]);
  const onCancelCard = () => setMessages((m) => [...m, { role: 'assistant', text: 'No problem — cancelled. Nothing was changed.' }]);

  const startLiveRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const res = await apiService.assistantTranscribe(blob);
          setInput(res.data?.text || '');
        } catch (_) {
          setInput('');
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const toggleMic = () => {
    if (live) {
      if (listening && mediaRef.current) { mediaRef.current.stop(); return; }
      startLiveRecording();
      return;
    }
    // mock mode
    if (listening) { setListening(false); return; }
    setListening(true);
    setTimeout(() => {
      setListening(false);
      setTranscribing(true);
      setTimeout(() => {
        setTranscribing(false);
        setInput("Let's pay Johnson Plumbing $5,000 as a partial payment for work completed on 1615 38th St");
      }, 1300);
    }, 1800);
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div ref={scrollRef} className={`bg-white overflow-y-auto p-4 space-y-4 ${heightClass}`}>
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-slate-200 text-slate-600' : 'text-white'}`} style={m.role === 'assistant' ? { background: NAVY } : undefined}>
              {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className={m.card ? 'max-w-[88%]' : 'max-w-[80%]'}>
              {m.thinking ? (
                <div className="rounded-2xl bg-slate-100 px-4 py-3 flex items-center gap-2 text-slate-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
                </div>
              ) : m.card ? (
                <ActionCard msg={m} onConfirm={onConfirmCard} onCancel={onCancelCard} live={live} userId={userId} smartReceipt={smartReceipt} />
              ) : (
                <div className={`rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-800'}`}>
                  <RichText text={m.text} />
                  {m.report && (
                    <button
                      onClick={() => {
                        const url = m.report.kind === 'portfolio'
                          ? apiService.portfolioStatementPdfUrl(m.report.user_id || userId)
                          : m.report.kind === 'interest'
                            ? apiService.projectInterestSummaryPdfUrl(m.report.project_id)
                            : apiService.projectStatementPdfUrl(m.report.project_id);
                        window.open(url, '_blank');
                      }}
                      data-testid="assistant-report-download"
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white"
                      style={{ background: NAVY }}
                    >
                      <FileDown className="w-4 h-4" /> Download {m.report.label} (PDF)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showExamples && (
        <div className="flex flex-wrap gap-2 px-4 pt-3 border-t border-slate-100">
          {EXAMPLES.map((ex, i) => {
            const icons = [Calendar, DollarSign, Receipt, HardHat];
            const Ico = icons[i];
            return (
              <button key={i} onClick={() => submit(ex)} data-testid={`assistant-example-${i}`}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition">
                <Ico className="w-3.5 h-3.5 text-slate-400" /> {ex.length > 40 ? ex.slice(0, 38) + '…' : ex}
              </button>
            );
          })}
        </div>
      )}

      <div className="px-4 py-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 flex items-end gap-2">
          <button onClick={toggleMic} data-testid="assistant-mic"
            className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
            <Mic className="w-5 h-5" />
          </button>
          <textarea
            data-testid="assistant-input" rows={1}
            value={transcribing ? '' : input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={listening ? 'Listening…' : transcribing ? 'Transcribing your voice…' : 'Ask anything, or tell me what to do…'}
            className="flex-1 resize-none bg-transparent px-2 py-2.5 text-sm focus:outline-none text-slate-800 placeholder:text-slate-400"
          />
          <button onClick={() => submit()} disabled={!input.trim()} data-testid="assistant-send"
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white disabled:opacity-40" style={{ background: NAVY }}>
            <Send className="w-5 h-5" />
          </button>
        </div>
        {showFootnote && (
          <p className="text-center text-[11px] text-slate-400 mt-2 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Every money action asks you to confirm before it commits.
          </p>
        )}
      </div>
    </div>
  );
}

export default AssistantChat;
